import React, { useEffect, useState } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar } from "react-chartjs-2";
import styled from "styled-components";
import { Tooltip as InfoTooltip } from "react-tooltip";
import { Info } from "lucide-react";

// Register chart.js modules
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend
);

const ChartWrapper = styled.div`
  position: relative;
  background-color: #f9f9f9;
  width: 100%;
  height: 40vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 20px;
`;

const InfoIconWrapper = styled.div`
  position: absolute;
  top: 10px;
  right: 15px;
  cursor: pointer;
  color: #666;
  transition: color 0.3s;

  &:hover {
    color: #000;
  }

  svg {
    width: 20px;
    height: 20px;
  }
`;

const FiltersWrapper = styled.div`
  display: flex;
  gap: 20px;
  margin-bottom: 20px;
  justify-content: center;
`;

const monthNamesPL = [
  "Styczeń", "Luty", "Marzec", "Kwiecień", "Maj", "Czerwiec",
  "Lipiec", "Sierpień", "Wrzesień", "Październik", "Listopad", "Grudzień"
];

const DailyComparisonChart = ({ data }) => {
  const [chartData, setChartData] = useState(null);
  const [error, setError] = useState(null);
  const [availableFilters, setAvailableFilters] = useState({
    months: [],
    years: [],
  });
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [selectedYear, setSelectedYear] = useState(null);

  // Calculate sales
  const calculateSales = (row) =>
    Math.max(
      0,
      parseFloat(
        row.K_15 ||
          row.K_19 ||
          row.K_10 ||
          row.K_21 ||
          row.K_17 ||
          row.K_11 ||
          row.K_22
      ) || 0
    );

  // Initialize filters & pick most recent month with data
  useEffect(() => {
    if (data && data.length > 0) {
      const months = new Set();
      const years = new Set();
      const monthYearSalesMap = {}; // { 'YYYY-MM': totalSales }

      data.forEach((row) => {
        const year = parseInt(row.Rok, 10);
        const month = parseInt(row.Miesiac, 10);
        if (Number.isNaN(year) || Number.isNaN(month)) return;

        months.add(month);
        years.add(year);

        const key = `${year}-${month}`;
        const sales = calculateSales(row);
        monthYearSalesMap[key] = (monthYearSalesMap[key] || 0) + sales;
      });

      const sortedYears = Array.from(years).sort((a, b) => a - b);
      const sortedMonths = Array.from(months).sort((a, b) => a - b);

      setAvailableFilters({ months: sortedMonths, years: sortedYears });

      // Most recent month/year with sales > 0
      const recent = Object.entries(monthYearSalesMap)
        .filter(([_, val]) => val > 0)
        .sort((a, b) => {
          const [yearA, monthA] = a[0].split("-").map(Number);
          const [yearB, monthB] = b[0].split("-").map(Number);
          return yearB - yearA || monthB - monthA;
        })[0];

      if (recent) {
        const [y, m] = recent[0].split("-").map(Number);
        setSelectedYear(y);
        setSelectedMonth(m);
      }
    } else {
      setError("Brak danych sprzedaży.");
    }
  }, [data]);

  // Build chart when month/year selected
  useEffect(() => {
    if (!(data && selectedMonth && selectedYear)) return;

    const daysInMonth = new Date(selectedYear, selectedMonth, 0).getDate();
    const days = Array.from({ length: daysInMonth }, (_, i) => i + 1);

    const aggCurrent = data.reduce((acc, row) => {
      const dateStr = row.DataWystawienia || "";
      const parts = dateStr.split("-");
      const day = parseInt(parts[2], 10);
      const month = parseInt(row.Miesiac, 10);
      const year = parseInt(row.Rok, 10);

      if (!Number.isNaN(day) && month === selectedMonth && year === selectedYear) {
        acc[day] = (acc[day] || 0) + calculateSales(row);
      }
      return acc;
    }, {});

    const currentSales = days.map((d) => aggCurrent[d] || 0);

    const prevYear = selectedYear - 1;
    const aggPrev = data.reduce((acc, row) => {
      const dateStr = row.DataWystawienia || "";
      const parts = dateStr.split("-");
      const day = parseInt(parts[2], 10);
      const month = parseInt(row.Miesiac, 10);
      const year = parseInt(row.Rok, 10);

      if (!Number.isNaN(day) && month === selectedMonth && year === prevYear) {
        acc[day] = (acc[day] || 0) + calculateSales(row);
      }
      return acc;
    }, {});
    const prevSales = days.map((d) => aggPrev[d] || 0);

    const GREEN = "#2e7d32";
    const ORANGE = "#fb8c00";

    setChartData({
      labels: days.map((d) => `${d}/${String(selectedMonth).padStart(2, "0")}`),
      datasets: [
        {
          label: `Sprzedaż (${selectedYear})`,
          data: currentSales,
          type: "bar",
          backgroundColor: "rgba(46, 125, 50, 0.70)", // uniform green bars
          borderColor: GREEN,
          borderWidth: 1,
        },
        {
          label: `Trend (${selectedYear})`,
          data: currentSales,
          type: "line",
          borderColor: GREEN,                 // current-year line = green
          backgroundColor: "rgba(46,125,50,0.15)",
          fill: false,
          tension: 0.3,
          pointBackgroundColor: GREEN,
        },
        {
          label: `Trend (${prevYear})`,
          data: prevSales,
          type: "line",
          borderColor: ORANGE,                // previous-year line = orange
          backgroundColor: "rgba(251,140,0,0.15)",
          fill: false,
          tension: 0.3,
          pointBackgroundColor: ORANGE,
        },
      ],
    });
  }, [data, selectedMonth, selectedYear]);

  const handleMonthChange = (e) => setSelectedMonth(parseInt(e.target.value, 10));
  const handleYearChange = (e) => setSelectedYear(parseInt(e.target.value, 10));

  if (!availableFilters.months.length || !availableFilters.years.length)
    return <p>Ładowanie filtrów...</p>;
  if (!chartData) return <p>Ładowanie danych...</p>;

  return (
    <ChartWrapper>
      {/* ℹ️ Info Icon + Tooltip */}
      <InfoIconWrapper data-tooltip-id="daily-comparison-tooltip">
        <Info />
      </InfoIconWrapper>
      <InfoTooltip
        id="daily-comparison-tooltip"
        place="left"
        content={
          <>
            <strong>Sprzedaż dzienna</strong>
            <br />
            Wykres przedstawia dzienną wartość sprzedaży
            <br />
            w miesiącu {monthNamesPL[selectedMonth - 1]} {selectedYear} oraz porównanie z {selectedYear - 1}.
          </>
        }
      />

      {error && (!chartData || chartData.labels.length === 0) && (
        <p style={{ color: "red" }}>{error}</p>
      )}

      <FiltersWrapper>
        <div>
          <label htmlFor="month-select">Miesiąc:</label>
          <select id="month-select" value={selectedMonth ?? ""} onChange={handleMonthChange}>
            {availableFilters.months.map((month) => (
              <option key={month} value={month}>
                {monthNamesPL[month - 1]}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="year-select">Rok:</label>
          <select id="year-select" value={selectedYear ?? ""} onChange={handleYearChange}>
            {availableFilters.years.map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>
        </div>
      </FiltersWrapper>

      <Bar
        data={chartData}
        options={{
          responsive: true,
          maintainAspectRatio: false,
          scales: {
            y: {
              beginAtZero: true,
              title: { display: true, text: "Sprzedaż" },
              ticks: {
                callback: function (value) {
                  return Number(value)
                    .toFixed(2)
                    .replace(/\B(?=(\d{3})+(?!\d))/g, " ");
                },
              },
            },
          },
          plugins: {
            title: {
              display: true,
              text: `Sprzedaż dzienna w miesiącu (${selectedMonth}/${selectedYear})`,
            },
            legend: {
              display: true,
              onClick: (e, legendItem, legend) => {
                const index = legendItem.datasetIndex;
                const ci = legend.chart;
                if (ci?.data?.datasets[index]) {
                  ci.isDatasetVisible(index) ? ci.hide(index) : ci.show(index);
                  ci.update();
                }
              },
            },
            tooltip: {
              callbacks: {
                label: function (context) {
                  const label = context.dataset.label || "";
                  const value = context.parsed.y || 0;
                  const formattedValue = Number(value)
                    .toFixed(2)
                    .replace(/\B(?=(\d{3})+(?!\d))/g, " ");
                  return `${label}: ${formattedValue}`;
                },
              },
            },
          },
        }}
      />
    </ChartWrapper>
  );
};

export default DailyComparisonChart;
